import { Moment, MomentInput } from 'moment';
import React, {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
  FC,
  useRef,
} from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { useDispatch, Provider } from 'react-redux';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { Checkbox } from 'components/atoms/checkbox';
import { confirm } from 'components/atoms/modal';
import { DependencyConflicModal } from 'components/molecules/tasks/dependency-modal/dependency-conflict-modal';
import { formatDate } from 'components/organisms/projects/project-plan/timeline/helpers';
import { useDependencyModalStates } from 'components/organisms/projects/project-plan/timeline/use-dependency-modal-states';
import { useListData } from 'components/organisms/projects/project-plan/use-list-data';
import type { DateField } from 'components/organisms/tasks/fields';
import { store } from 'data';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import { useGetAlignPhaseDatesSetting } from 'queries/advanced-settings/advanced-settings';
import type { Task } from 'stores/tasks';
import translations from 'translations/en.json';
import {
  DateType,
  Entity,
  EntityType,
  getDateShiftsForEntity,
  getDuration,
  getNewDates,
} from 'utils/date-shift';
import { DateFieldEditorPopover } from './date-field-editor-popover';

const DateEditor = styled(DateFieldEditorPopover)`
  padding: 0 !important;
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: calc(100% + var(--spacing-06) + var(--spacing-07));
  z-index: var(--zindex-layer-09);
`;

interface Props {
  data: Task;
  title: ReactNode;
  onChange: any;
  ref: RefObject<any>;
  field: DateField;
  disabledDate: (current: Moment) => boolean;
  allowClear?: boolean;
  isCustomField: boolean;
  dependencyModalVisible: boolean;
  onDependencyModalToggle: (value: boolean) => void;
}

const StyledDateEditor = styled(DateEditor)<{
  maintainDuration: boolean;
  isCustomField: boolean;
}>`
  height: ${(props) =>
    props.maintainDuration && !props.isCustomField && '440px'};
`;

export const AGGridDateEditor: FC<Props> = (props) => {
  const {
    title,
    field,
    onChange,
    disabledDate,
    data,
    allowClear = true,
    dependencyModalVisible,
    onDependencyModalToggle,
  } = props;
  const dispatch = useDispatch();
  const [haveMaintainDurationCheckbox, setHaveMaintainDurationCheckbox] =
    useState(false);

  const location = useLocation();
  const { taskList, phaseList, sprintList, tasks, projectPhases, sprints } =
    useListData(data.project);

  const { setDependencyModalTimeout } = useDependencyModalStates(
    taskList,
    phaseList,
    sprintList,
  );

  const translate = useTranslate();
  const alignPhaseDates = useGetAlignPhaseDatesSetting();

  const timeOutStateRef = useRef(false);

  const path = useCallback(() => {
    if (
      location.pathname === `/projects/${data.project}/tasks/list` ||
      location.pathname === '/tasks'
    ) {
      setHaveMaintainDurationCheckbox(true);
    } else {
      setHaveMaintainDurationCheckbox(false);
    }
  }, [data.project, location.pathname]);

  useEffect(() => {
    path();
  }, [data.project, path]);

  const [checked, setChecked] = useLocalStorage(
    'use.maintain.duration.local.storage',
    true,
  );

  const handleCheckBoxChange = () => {
    setChecked(!checked);
  };

  const _duration = useMemo(
    () => getDuration(data.startDate, data.dueDate, false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.startDate, data.dueDate],
  );

  const handleDateChange = useCallback(
    (latest) => {
      if (haveMaintainDurationCheckbox && checked && !field.customField) {
        const payload: { [key: string]: MomentInput | null } = {};

        if (latest !== null) {
          const [newStartDate, newDueDate] = getNewDates(
            latest,
            _duration,
            field.fieldName as unknown as DateType,
          );

          payload.startDate = newStartDate;
          payload.dueDate = newDueDate;
        } else if (field.fieldName === DateType.StartDate) {
          payload.startDate = null;
          payload.dueDate = formatDate(data.dueDate);
        } else {
          payload.startDate = formatDate(data.startDate);
          payload.dueDate = null;
        }

        dispatch(
          updateTaskSagaAction({
            projectId: data.project,
            taskId: data.taskId,
            data: payload,
          }),
        );
      }
      onChange(latest);
    },
    [
      _duration,
      checked,
      data.dueDate,
      data.project,
      data.startDate,
      data.taskId,
      dispatch,
      field.customField,
      field.fieldName,
      haveMaintainDurationCheckbox,
      onChange,
    ],
  );

  const checkboxHandler = useCallback((event) => {
    timeOutStateRef.current = event.target.checked;
  }, []);

  const modal = useCallback(
    (taskUpdates, phaseUpdates, sprintUpdates, entity, latest) => {
      confirm({
        title: translate('task.dependency.conflicts'),
        content: (
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <IntlProvider locale="en" messages={translations}>
                <Wrapper>
                  <DependencyConflicModal
                    visible={dependencyModalVisible}
                    onClose={() => onDependencyModalToggle(false)}
                    loading={false}
                    projectId={data.project}
                    taskUpdates={taskUpdates}
                    phaseUpdates={phaseUpdates}
                    sprintUpdates={sprintUpdates}
                    entity={entity}
                    showContent
                    projectPhases={projectPhases}
                    tasks={tasks}
                    sprints={sprints ?? []}
                    taskList={taskList}
                    sprintList={sprintList}
                    phaseList={phaseList}
                    setDependencyModalTimeout={setDependencyModalTimeout}
                  />
                  <CheckboxWrapper>
                    <Checkbox onChange={checkboxHandler}>
                      <FormattedMessage id="task.dependency.conflicts.disable.modal" />
                    </Checkbox>
                  </CheckboxWrapper>
                </Wrapper>
              </IntlProvider>
            </Provider>
          </QueryClientProvider>
        ),
        width: '696px',
        okText: translate('confirm'),
        okType: 'primary',
        cancelText: translate('cancel'),
        onCancel: () => {
          onDependencyModalToggle(false);
        },
        onOk: () => {
          handleDateChange(latest);
          onDependencyModalToggle(false);
          if (timeOutStateRef.current) {
            setDependencyModalTimeout(true);
          }
        },
      });
    },
    [
      checkboxHandler,
      data.project,
      dependencyModalVisible,
      handleDateChange,
      onDependencyModalToggle,
      projectPhases,
      setDependencyModalTimeout,
      sprints,
      tasks,
      translate,
      phaseList,
      sprintList,
      taskList,
    ],
  );

  const handleOnChange = useCallback(
    (latest: string | null) => {
      if (field.customField) {
        handleDateChange(latest);

        return;
      }

      if (!latest && dependencyModalVisible) {
        return;
      }
      if (data.taskId) {
        const entity: Entity = {
          id: 0,
          startDate: null,
          dueDate: null,
          type: EntityType.Task,
          depth: 0,
          name: '',
        };

        entity.id = data.taskId;
        if (latest !== null) {
          // checks for maintain duration checkbox
          if (haveMaintainDurationCheckbox && checked && !field.customField) {
            const [newStartDate, newDueDate] = getNewDates(
              latest,
              _duration,
              field.fieldName as unknown as DateType,
            );

            entity.startDate = newStartDate;
            entity.dueDate = newDueDate;
          } else if (field.fieldName === DateType.StartDate) {
            entity.startDate = latest;
            entity.dueDate = formatDate(data.dueDate);
          } else {
            entity.startDate = formatDate(data.startDate);
            entity.dueDate = latest;
          }
        } else if (field.fieldName === DateType.StartDate) {
          entity.startDate = null;
          entity.dueDate = formatDate(data.dueDate);
        } else {
          entity.startDate = formatDate(data.startDate);
          entity.dueDate = null;
        }
        entity.type = EntityType.Task;
        entity.depth = taskList[data.taskId].depth;

        const [
          phaseUpdates,
          taskUpdates,
          sprintUpdates,
          doesEntityAffectOtherEntities,
        ] = getDateShiftsForEntity(phaseList, taskList, sprintList, entity, {
          alignPhaseWithTask: alignPhaseDates,
        });

        if (doesEntityAffectOtherEntities) {
          onDependencyModalToggle?.(true);
          entity.name = taskList[data.taskId].name;
          modal(taskUpdates, phaseUpdates, sprintUpdates, entity, latest);
        } else {
          handleDateChange(latest);
        }
      } else {
        handleDateChange(latest);
      }
    },
    [
      _duration,
      alignPhaseDates,
      checked,
      data.dueDate,
      data.startDate,
      data.taskId,
      dependencyModalVisible,
      field.customField,
      field.fieldName,
      handleDateChange,
      haveMaintainDurationCheckbox,
      modal,
      onDependencyModalToggle,
      phaseList,
      sprintList,
      taskList,
    ],
  );

  return (
    <StyledDateEditor
      isCustomField={!!(field.customField && field.userCreated)}
      title={title}
      field={field}
      onChange={handleOnChange}
      disabledDate={
        !haveMaintainDurationCheckbox
          ? disabledDate
          : !checked
          ? disabledDate
          : undefined
      }
      allowClear={allowClear}
      maintainDuration={haveMaintainDurationCheckbox}
      extraFooter={() => {
        return (
          haveMaintainDurationCheckbox &&
          !field.customField && (
            <Checkbox checked={checked} onChange={handleCheckBoxChange}>
              <FormattedMessage id="date.picker.maintain.duration" />
            </Checkbox>
          )
        );
      }}
    />
  );
};
